import React, { useEffect, useRef, useState } from "react";
import { MASTER_API } from "../../../apiConfig";
import sbeeLogo from "../../../assets/sbeelogo.png";
import { PURCHASE_API } from "../../../apiConfig";
import { useParams } from "react-router-dom";
import { authHeaders } from "../../../utils/authHeaders"; // ⚠️ ADJUST if this file's actual depth differs


function PODocsView() {
  const formatDDMMYYYY = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // Formats a number the Indian way: 4,60,200.00 (lakh/crore grouping)
  // instead of the default 460,200.00 (thousand grouping).
  const formatINR = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString(docCurrency.isHome ? "en-IN" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Unit Cost / Rate is kept to 3 decimals (matches the PO form),
  // while amounts (Total, Net Price, taxes) stay at 2 decimals via formatINR.
  const formatRate = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString(docCurrency.isHome ? "en-IN" : "en-US", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  // Pulls the "Supplier Description" characteristic value off a PO line —
  // same characteristic used by the Purchase Order / Goods Receipt screens
  // (Product Class's Supplier Description), handling the same double
  // -stringified-JSON quirk those screens guard against.
  const getSupplierDescription = (item) => {
    let chars = item?.characteristics || item?.Characteristics || [];
    if (typeof chars === "string") {
      try { chars = JSON.parse(chars); } catch { chars = []; }
    }
    if (typeof chars === "string") {
      try { chars = JSON.parse(chars); } catch { chars = []; }
    }
    if (!Array.isArray(chars)) return "";

    const match = chars.find(
      (c) => (c.characteristic || c.name || "").trim().toLowerCase() === "supplier description"
    );
    return match?.value || "";
  };

  // Horizontal padding kept tight and long values allowed to wrap, so the
  // material grid never grows wider than the page box — its right edge then
  // lines up with the Taxable Amount summary and the right margin below it.
  const cellStyle = {
    border: "1px solid #000",
    padding: "5px 3px",
    overflowWrap: "break-word",
    fontFamily: "'Courier Prime', 'Courier New', monospace",
    fontSize: "12px",
  };
  const [bpData, setBpData] = useState({});
  const { poId } = useParams(); // ✅ only one declaration
  const [poData, setPoData] = useState([]);
  // Download / Print are allowed only once every line of the PO is approved
  // (the server enforces this too — see requireApprovedPO).
  const isPoApproved =
    poData?.length > 0 &&
    poData.every((r) => String(r.review || "").toUpperCase() === "APPROVED");
  //eslint-disable-next-line
  const [vendorData, setVendorData] = useState({});

// 🆕 Currency master (Configuration > Currency) — symbol/code per currency,
// so an Import PO prints in its own currency instead of a fixed INR ₹.
const [currencyMaster, setCurrencyMaster] = useState([]);
useEffect(() => {
  fetch(`${MASTER_API}/configuration/dynamic/currency`, { headers: authHeaders() })
    .then((r) => (r.ok ? r.json() : []))
    .then((d) => setCurrencyMaster(Array.isArray(d) ? d : []))
    .catch((err) => console.error("Currency master error:", err));
}, []);
  const [showDocs, setShowDocs] = useState(false);

  // ── PO Terms & Conditions ──────────────────────────────────────────────
  // poTemplateSections = the SHARED default template from master-service
  //   (Configuration > Terms & Conditions > Purchase Order tab). Applies to
  //   every PO that hasn't been individually edited.
  // savedPoSections = THIS PO's own saved override (if any), from
  //   purchase-service. Takes priority over the shared template when present.
  // Editing + Saving here writes ONLY to this PO's override — it never
  // touches the shared Config template. Editing the shared template in
  // Config never touches a PO that already has its own override.
  const [poTemplateSections, setPoTemplateSections] = useState([]);
  const [savedPoSections, setSavedPoSections] = useState(null); // null = no override yet
  const [templateLoading, setTemplateLoading] = useState(false);
  const [editingPoTerms, setEditingPoTerms] = useState(false);
  const [poTermsDraft, setPoTermsDraft] = useState([]); // working copy while editing
  const [savingPoTerms, setSavingPoTerms] = useState(false);

  // Inline edit of the PO's short "Terms and Conditions" text
  // (purchase_orders.terms_and_conditions) shown in the main preview block.
  const [editingTermsText, setEditingTermsText] = useState(false);
  const [termsTextDraft, setTermsTextDraft] = useState("");
  const [savingTermsText, setSavingTermsText] = useState(false);

  const handleEditTermsText = () => {
    setTermsTextDraft(poData?.[0]?.terms_and_conditions || "");
    setEditingTermsText(true);
  };

  const handleCancelTermsText = () => {
    setEditingTermsText(false);
    setTermsTextDraft("");
  };

  const saveTermsText = async () => {
    try {
      setSavingTermsText(true);
      const res = await fetch(`${PURCHASE_API}/po/${poId}/terms-text`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ terms_and_conditions: termsTextDraft }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Failed to save Terms and Conditions");
      await loadPO(); // refresh so the preview/PDF show the saved text
      setEditingTermsText(false);
    } catch (err) {
      console.error("SAVE TERMS TEXT ERROR", err);
      alert(err.message || "Failed to save Terms and Conditions. Please try again.");
    } finally {
      setSavingTermsText(false);
    }
  };

  // What's actually shown: this PO's own saved override if it has one,
  // otherwise the shared Config template.
  const effectivePoSections = savedPoSections || poTemplateSections;

  const loadPoTerms = async () => {
    try {
      setTemplateLoading(true);

      // 1. Shared default template (Config)
      const tRes = await fetch(`${MASTER_API}/configuration/templates/po`, {
        headers: authHeaders(),
      });
      const tJson = tRes.ok ? await tRes.json() : null;
      const templateSections = Array.isArray(tJson?.content?.sections) ? tJson.content.sections : [];
      setPoTemplateSections(templateSections);

      // 2. This PO's own override, if it has been saved before
      const oRes = await fetch(`${PURCHASE_API}/po/${poId}/terms`, {
        headers: authHeaders(),
      });
      if (oRes.ok) {
        const oJson = await oRes.json();
        setSavedPoSections(Array.isArray(oJson?.sections) ? oJson.sections : null);
      } else {
        setSavedPoSections(null); // 404 = no override saved yet, fall back to template
      }
    } catch (err) {
      console.error("PO TERMS LOAD ERROR", err);
      setPoTemplateSections([]);
      setSavedPoSections(null);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleEditPoTerms = () => {
    // Deep-clone whatever is currently shown (override if present, else
    // template) so edits start from what the user actually sees.
    setPoTermsDraft(JSON.parse(JSON.stringify(effectivePoSections)));
    setEditingPoTerms(true);
  };

  const handleCancelPoTerms = () => {
    setEditingPoTerms(false);
    setPoTermsDraft([]);
  };

  // Drops rows that were left blank, so no empty numbered box is ever saved
  // (numbering then closes up automatically, since it's just array order).
  const cleanTermsDraft = (sections) =>
    (sections || []).map((sec) => {
      if (sec.type === "list") {
        return { ...sec, items: (sec.items || []).filter((t) => (t || "").trim() !== "") };
      }
      return {
        ...sec,
        clauses: (sec.clauses || []).map((c) => ({
          ...c,
          paragraphs: (c.paragraphs || []).filter((t) => (t || "").trim() !== ""),
        })),
      };
    });

  const savePoTermsOverride = async () => {
    try {
      setSavingPoTerms(true);
      const cleaned = cleanTermsDraft(poTermsDraft);
      const res = await fetch(`${PURCHASE_API}/po/${poId}/terms`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ sections: cleaned }),
      });
      if (!res.ok) throw new Error("Failed to save PO terms");
      const saved = await res.json();
      setSavedPoSections(Array.isArray(saved?.sections) ? saved.sections : cleaned);
      setEditingPoTerms(false);
      alert("Saved — this Terms & Conditions text now applies to this PO only.");
    } catch (err) {
      console.error("SAVE PO TERMS ERROR", err);
      alert("Failed to save Terms & Conditions for this PO. Please try again.");
    } finally {
      setSavingPoTerms(false);
    }
  };

  // Draft editing helpers — list-type sections (plain numbered items)
  const updateDraftListItem = (sIdx, iIdx, value) => {
    setPoTermsDraft((prev) => {
      const next = [...prev];
      const items = [...(next[sIdx].items || [])];
      items[iIdx] = value;
      next[sIdx] = { ...next[sIdx], items };
      return next;
    });
  };

  // Draft editing helpers — clause-type sections (numbered clauses with paragraphs)
  const updateDraftParagraph = (sIdx, cIdx, pIdx, value) => {
    setPoTermsDraft((prev) => {
      const next = [...prev];
      const clauses = [...(next[sIdx].clauses || [])];
      const paragraphs = [...(clauses[cIdx].paragraphs || [])];
      paragraphs[pIdx] = value;
      clauses[cIdx] = { ...clauses[cIdx], paragraphs };
      next[sIdx] = { ...next[sIdx], clauses };
      return next;
    });
  };

  // Add / delete a row — list-type sections. Deleting splices the item out
  // of the array, so the <ol> renumbers itself (delete #3 -> old #4 becomes #3).
  const addDraftListItem = (sIdx, afterIdx) => {
    setPoTermsDraft((prev) => {
      const next = [...prev];
      const items = [...(next[sIdx].items || [])];
      items.splice(afterIdx + 1, 0, "");
      next[sIdx] = { ...next[sIdx], items };
      return next;
    });
  };

  const removeDraftListItem = (sIdx, iIdx) => {
    setPoTermsDraft((prev) => {
      const next = [...prev];
      const items = [...(next[sIdx].items || [])];
      items.splice(iIdx, 1);
      next[sIdx] = { ...next[sIdx], items };
      return next;
    });
  };

  // Add / delete a paragraph — clause-type sections
  const addDraftParagraph = (sIdx, cIdx, afterIdx) => {
    setPoTermsDraft((prev) => {
      const next = [...prev];
      const clauses = [...(next[sIdx].clauses || [])];
      const paragraphs = [...(clauses[cIdx].paragraphs || [])];
      paragraphs.splice(afterIdx + 1, 0, "");
      clauses[cIdx] = { ...clauses[cIdx], paragraphs };
      next[sIdx] = { ...next[sIdx], clauses };
      return next;
    });
  };

  const removeDraftParagraph = (sIdx, cIdx, pIdx) => {
    setPoTermsDraft((prev) => {
      const next = [...prev];
      const clauses = [...(next[sIdx].clauses || [])];
      const paragraphs = [...(clauses[cIdx].paragraphs || [])];
      paragraphs.splice(pIdx, 1);
      clauses[cIdx] = { ...clauses[cIdx], paragraphs };
      next[sIdx] = { ...next[sIdx], clauses };
      return next;
    });
  };

  // Small inline-SVG icon buttons (no icon library needed)
  const rowIconBtn = (color) => ({
    border: "none",
    background: "transparent",
    padding: "2px",
    cursor: "pointer",
    color,
    lineHeight: 0,
  });
  const PlusIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="3" x2="8" y2="13" />
      <line x1="3" y1="8" x2="13" y2="8" />
    </svg>
  );
  const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 4h11M6 4V2.5h4V4M4 4l.6 9.5h6.8L12 4M6.5 6.5v5M9.5 6.5v5" />
    </svg>
  );
  const RowActions = ({ onAdd, onRemove }) => (
    <div style={{ display: "flex", gap: "2px", alignItems: "center", flexShrink: 0 }}>
      <button type="button" title="Add row below" aria-label="Add row below" style={rowIconBtn("#198754")} onClick={onAdd}>
        <PlusIcon />
      </button>
      <button type="button" title="Delete this row" aria-label="Delete this row" style={rowIconBtn("#dc3545")} onClick={onRemove}>
        <TrashIcon />
      </button>
    </div>
  );

  const poWrapRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office) — used for letterhead, Bill to, and Ship to

  // Free-text note saved for this whole PO (stored server-side as JSON,
  // reusing the product-notes endpoint under one fixed key since a PO can be
  // saved just once regardless of how many product lines it has).
  const PO_NOTE_KEY = "__PO_NOTE__";
  //eslint-disable-next-line
  const [poNote, setPoNote] = useState("");       // last saved value from server
  const [poNoteDraft, setPoNoteDraft] = useState(""); // live editing value
  const [savingNote, setSavingNote] = useState(false);

  // Bill to / Ship to — per-PO override of the default Unit 1 address.
  // Reuses the same product-notes endpoint/table as the PO Note above (one
  // fixed key per field, same as PO_NOTE_KEY), so no new table is needed.
  // Empty string = nothing saved for this PO yet -> falls back to
  // unit1AddressLines (Location Master default).
  const PO_BILL_TO_KEY = "__PO_BILL_TO__";
  const PO_SHIP_TO_KEY = "__PO_SHIP_TO__";
  const [billTo, setBillTo] = useState("");         // last saved value from server
  const [billToDraft, setBillToDraft] = useState(""); // live editing value
  const [editingBillTo, setEditingBillTo] = useState(false);
  const [savingBillTo, setSavingBillTo] = useState(false);
  const [shipTo, setShipTo] = useState("");
  const [shipToDraft, setShipToDraft] = useState("");
  const [editingShipTo, setEditingShipTo] = useState(false);
  const [savingShipTo, setSavingShipTo] = useState(false);

  // Which line items appear on the printed/downloaded PO form:
  //  • Before any line has been approved (Created / Pending / Query …) → ALL lines,
  //    so the form isn't empty while the PO is still going through approval.
  //  • Once at least one line is approved → ONLY the approved lines
  //    (pending/rejected lines drop off).
  const hasApprovedLine = (poData || []).some(
    (item) => String(item.review || "").toUpperCase() === "APPROVED"
  );
  const displayItems = hasApprovedLine
    ? (poData || []).filter(
      (item) => String(item.review || "").toUpperCase() === "APPROVED"
    )
    : (poData || []);

  // 🆕 Currency this PO is denominated in: Import PO -> its import_currency,
// otherwise home currency INR. Symbol comes from the Currency master; if the
// currency isn't in the master we show its text, never a wrong ₹.
const docCurrency = (() => {
  const head = poData?.[0] || {};
  const isImport = String(head.order_type || "").toLowerCase() === "import";
  // 🐛 FIX: use the currency actually saved on the PO lines (the Product
  // tab's Currency column; all lines share one). Previously this only
  // looked at import_currency when order_type was "import", so a PO whose
  // lines were in USD/EURO but whose header wasn't flagged Import (or had
  // no import_currency) always printed INR ₹ / "Rupees Only".
  const lineCurrency = String(
    (displayItems || []).find((i) => i && i.currency)?.currency || head.currency || ""
  ).trim();
  const raw = String(
    lineCurrency || (isImport && head.import_currency) || "INR"
  ).trim();
  const key = raw.toLowerCase();
  const row = currencyMaster.find(
    (r) =>
      String(r.name || "").trim().toLowerCase() === key ||
      String(r.currency_code || "").trim().toLowerCase() === key
  );
  const isHome = key === "inr" || key === "rupee" || key === "indian rupee";
  return {
    isHome,
    // label = the currency name exactly as configured (e.g. INR, USD, Yuan)
    code: row?.name || (isHome ? "INR" : raw),
    name: row?.name || raw,
    // symbol from master; falls back to the Currency Code column if that is
    // where the symbol was entered
    symbol: row?.currency_symbol || row?.currency_code || (isHome ? "₹" : raw),
  };
})();

// Grand total (after tax) for this PO — computed once here so both the
  // "Total In Word" box and the Taxable Amount summary panel use the exact
  // same number instead of two independent calculations drifting apart.
  const grandTotalAfterTax = displayItems?.reduce(
    (sum, item) => sum + Number(item.final_price_after_gst || 0),
    0
  ) || 0;

  // ── Number → Indian-English words (for "Total In Word") ─────────────────
  // e.g. 654354.60 -> "Six Lakh Fifty Four Thousand Three Hundred Fifty
  // Four Rupees And Sixty Paisa Only"
  // 🆕 Amount in words for a foreign-currency (Import) PO — international
  // grouping (Thousand / Million / Billion) and the currency name as the
  // label, e.g. "One Thousand Two Hundred Fifty USD And Fifty Cents Only".
  const numberToForeignWords = (num, code) => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
      "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
    const three = (n) => {
      const h = Math.floor(n / 100), r = n % 100;
      return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
    };
    const whole = (n) => {
      if (n === 0) return "Zero";
      const parts = [];
      [[1e9, "Billion"], [1e6, "Million"], [1e3, "Thousand"]].forEach(([size, label]) => {
        const q = Math.floor(n / size);
        if (q) parts.push(three(q) + " " + label);
        n %= size;
      });
      if (n) parts.push(three(n));
      return parts.join(" ").trim();
    };

    // Minor-unit name per currency (what comes after the decimal point).
    // Anything not listed falls back to "Cent/Cents".
    const MINOR_UNITS = {
      usd: ["Cent", "Cents"], "us dollar": ["Cent", "Cents"], dollar: ["Cent", "Cents"],
      eur: ["Cent", "Cents"], euro: ["Cent", "Cents"],
      sgd: ["Cent", "Cents"], aud: ["Cent", "Cents"], cad: ["Cent", "Cents"],
      nzd: ["Cent", "Cents"], hkd: ["Cent", "Cents"],
      gbp: ["Penny", "Pence"], pound: ["Penny", "Pence"],
      cny: ["Fen", "Fen"], rmb: ["Fen", "Fen"], yuan: ["Fen", "Fen"],
      aed: ["Fils", "Fils"], dirham: ["Fils", "Fils"],
    };
    const minorLabels = MINOR_UNITS[String(code || "").trim().toLowerCase()] || ["Cent", "Cents"];

    // Round the whole amount to 2 decimals first, so floating-point noise
    // (e.g. 10.995 or 0.299999) can never produce a bad cents value like 100.
    const cents = Math.round(Math.abs(Number(num) || 0) * 100);
    const major = Math.floor(cents / 100);
    const minor = cents % 100;
    const label = code || "";

    return minor > 0
      ? `${whole(major)} ${label} And ${two(minor)} ${minor === 1 ? minorLabels[0] : minorLabels[1]} Only`
      : `${whole(major)} ${label} Only`;
  };

  const numberToIndianWords = (num) => {
    const ones = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
      "Seventeen", "Eighteen", "Nineteen",
    ];
    const tens = [
      "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
    ];

    const twoDigits = (n) => {
      if (n === 0) return "";
      if (n < 20) return ones[n];
      return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    };

    const threeDigits = (n) => {
      if (n === 0) return "";
      const hundred = Math.floor(n / 100);
      const rest = n % 100;
      return (hundred ? ones[hundred] + " Hundred" + (rest ? " " : "") : "") + twoDigits(rest);
    };

    const wholeToWords = (n) => {
      if (n === 0) return "Zero";
      const crore = Math.floor(n / 10000000);
      n %= 10000000;
      const lakh = Math.floor(n / 100000);
      n %= 100000;
      const thousand = Math.floor(n / 1000);
      n %= 1000;
      const hundred = n;

      const parts = [];
      if (crore) parts.push(threeDigits(crore) + " Crore");
      if (lakh) parts.push(twoDigits(lakh) + " Lakh");
      if (thousand) parts.push(twoDigits(thousand) + " Thousand");
      if (hundred) parts.push(threeDigits(hundred));
      return parts.join(" ").trim();
    };

    const amount = Math.abs(Number(num) || 0);
    const rupees = Math.floor(amount);
    // Round to nearest paisa to avoid floating-point remnants (e.g. 0.599999).
    const paise = Math.round((amount - rupees) * 100);

    const rupeeWords = wholeToWords(rupees);
    const rupeeLabel = rupees === 1 ? "Rupee" : "Rupees";

    if (paise > 0) {
      const paiseWords = wholeToWords(paise);
      const paiseLabel = paise === 1 ? "Paisa" : "Paise";
      return `${rupeeWords} ${rupeeLabel} And ${paiseWords} ${paiseLabel} Only`;
    }
    return `${rupeeWords} ${rupeeLabel} Only`;
  };

  // Load any previously saved note for this PO.
  useEffect(() => {
    const loadNote = async () => {
      if (!poId) return;
      try {
        const res = await fetch(
          `${PURCHASE_API}/po/product-notes?po_number=${encodeURIComponent(poId)}`,
          { headers: authHeaders() }
        );
        const data = await res.json();
        const savedNote = data?.data?.[PO_NOTE_KEY] || "";
        setPoNote(savedNote);
        setPoNoteDraft(savedNote);

        // Same response already contains any saved Bill to / Ship to
        // override for this PO (stored under fixed keys in the same
        // per-PO notes JSON) — pick them up here instead of a separate call.
        const savedBillTo = data?.data?.[PO_BILL_TO_KEY] || "";
        const savedShipTo = data?.data?.[PO_SHIP_TO_KEY] || "";
        setBillTo(savedBillTo);
        setBillToDraft(savedBillTo);
        setShipTo(savedShipTo);
        setShipToDraft(savedShipTo);
      } catch (err) {
        console.error("Failed to load PO note:", err);
      }
    };
    loadNote();
  }, [poId]);

  const savePoNote = async () => {
    setSavingNote(true);
    try {
      const res = await fetch(`${PURCHASE_API}/po/product-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          po_number: poId,
          product_code: PO_NOTE_KEY,
          notes: poNoteDraft,
        }),
      });
      const data = await res.json();
      if (data?.success) {
        setPoNote(poNoteDraft);
      } else {
        alert(data?.message || "Failed to save note");
      }
    } catch (err) {
      console.error("Failed to save PO note:", err);
      alert("Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  // Saves the Bill to / Ship to override for THIS PO only — every other PO
  // keeps using the Location Master default (unit1AddressLines) unless it
  // has its own saved override.
  const saveBillTo = async () => {
    setSavingBillTo(true);
    try {
      const res = await fetch(`${PURCHASE_API}/po/product-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          po_number: poId,
          product_code: PO_BILL_TO_KEY,
          notes: billToDraft,
        }),
      });
      const data = await res.json();
      if (data?.success) {
        setBillTo(billToDraft);
        setEditingBillTo(false);
      } else {
        alert(data?.message || "Failed to save Bill to address");
      }
    } catch (err) {
      console.error("Failed to save Bill to address:", err);
      alert("Failed to save Bill to address");
    } finally {
      setSavingBillTo(false);
    }
  };

  const saveShipTo = async () => {
    setSavingShipTo(true);
    try {
      const res = await fetch(`${PURCHASE_API}/po/product-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          po_number: poId,
          product_code: PO_SHIP_TO_KEY,
          notes: shipToDraft,
        }),
      });
      const data = await res.json();
      if (data?.success) {
        setShipTo(shipToDraft);
        setEditingShipTo(false);
      } else {
        alert(data?.message || "Failed to save Ship to address");
      }
    } catch (err) {
      console.error("Failed to save Ship to address:", err);
      alert("Failed to save Ship to address");
    } finally {
      setSavingShipTo(false);
    }
  };



  useEffect(() => {
    if (poId) loadPO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  useEffect(() => {
    const loadUnit1Location = async () => {
      try {
        const res = await fetch(`${MASTER_API}/location-master`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        const locations = Array.isArray(data) ? data : [];
        const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
        setUnit1Location(unit1);
      } catch (err) {
        console.error("LOCATION MASTER FETCH ERROR", err);
        setUnit1Location(null);
      }
    };
    loadUnit1Location();
  }, []);

  // Unit 1 (Regd. Office) name/address — used in the letterhead plus the
  // Bill to / Ship to blocks, sourced from Configuration > Location Master.
  // Falls back to the static text only if Location Master hasn't been set up yet.
  const unit1Name = unit1Location?.company_name || "SBEE CABLES INDIA LTD";
  const unit1AddressLines = unit1Location
    ? [
      unit1Location.address_line1,
      unit1Location.address_line2,
      `${unit1Location.city}${unit1Location.pin_code ? " " + unit1Location.pin_code : ""}, ${unit1Location.state}, ${unit1Location.country || "India"}`,
    ].filter(Boolean)
    : ["No.282 & 283", "KIADB Industrial Area, Phase 2, Harohalli,", "Kanakapura Taluk,", "Ramanagar 562112, Karnataka, India"];

  // Effective Bill to / Ship to lines for THIS PO: the saved per-PO override
  // (one line per newline typed in the textarea) if this PO has one, else
  // the Location Master default. Independent per field, so a PO can override
  // just Ship to (e.g. a different delivery site) while Bill to stays default.
  const billToAddressLines = billTo
    ? billTo.split("\n").map((l) => l.trim()).filter(Boolean)
    : unit1AddressLines;
  const shipToAddressLines = shipTo
    ? shipTo.split("\n").map((l) => l.trim()).filter(Boolean)
    : unit1AddressLines;

  // If opened as /po-docs/:poId?autoDownload=1 (used by the Docs tab's
  // Download button), fire the same exact-view download automatically
  // once the PO data has loaded, then close the tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoDownload") === "1" && poData?.length > 0 && !templateLoading) {
      const timer = setTimeout(() => {
        downloadExactView().then(() => {
          setTimeout(() => window.close(), 800);
        });
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poData, templateLoading]);

  // Belt-and-braces for the Print flow: don't rely solely on the
  // `@media print` CSS rule to reveal .print-only / .pdf-timestamp (some
  // embedded/webview browsers don't apply print media queries from a
  // dynamically-injected <style> tag reliably). This forces them visible
  // directly via inline style, and is called both (a) right before we
  // trigger window.print() ourselves for the autoPrint flow, and (b) on
  // the native 'beforeprint' event for anyone hitting Ctrl+P manually.
  function revealPrintOnlyContent() {
    document.querySelectorAll(".print-only, .pdf-timestamp").forEach((el) => {
      if (el.dataset.prevDisplay === undefined) el.dataset.prevDisplay = el.style.display || "";
      el.style.display = "block";
      if (el.classList.contains("pdf-timestamp")) {
        el.textContent = `Printed on: ${new Date().toLocaleString("en-IN")}`;
      }
    });
  }
  function restorePrintOnlyContent() {
    document.querySelectorAll(".print-only, .pdf-timestamp").forEach((el) => {
      el.style.display = el.dataset.prevDisplay ?? "";
      delete el.dataset.prevDisplay;
    });
  }

  // If opened as /po-docs/:poId?autoPrint=1 (used by the Docs tab's Print
  // button), trigger printExactView() only once the PO data has actually
  // loaded. Previously the Print button called printWindow.print() from
  // the parent's `onload` handler, which fires as soon as this page's
  // JS/shell loads — well before the async loadPO() fetch resolves — so
  // the print dialog opened against an empty table (0 rows). Gating on
  // poData?.length here guarantees the print always has real data.
  // printExactView() (defined below) prints the exact same server-rendered
  // PDF that Download produces, rather than the browser's native print of
  // the live page, so the long Terms & Conditions section paginates and
  // prints identically in both places.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoPrint") === "1" && poData?.length > 0 && !templateLoading) {
      const timer = setTimeout(() => {
        printExactView();
      }, 400);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poData, templateLoading]);

  const loadPO = async () => {
    try {
      const res = await fetch(
        `${PURCHASE_API}/po/${poId}`,
        { headers: authHeaders() }
      );

      const data = await res.json();

      console.log("PO DATA", data);

      // The endpoint may return a bare array, or an object wrapping the
      // rows (e.g. { data: [...] } / { po: [...] } / { rows: [...] }).
      // Normalize to an array either way so poData is never a non-array
      // object (which breaks poData.filter(...) below).
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.po)
            ? data.po
            : Array.isArray(data?.rows)
              ? data.rows
              : [];

      if (rows.length === 0 && !Array.isArray(data)) {
        console.error("PO load error: unexpected/empty response shape", data);
      }

      setPoData(rows);

      // Load vendor after PO loads
      const vendorId = rows?.[0]?.vendor_id;

      if (vendorId) {
        loadVendor(vendorId);
      }

    } catch (err) {
      console.error(
        "PO load error:",
        err
      );
      setPoData([]);
    }
  };

  // Turns "maithili05.m@gmail.com" -> "Maithili05 M", "admin@example.com" ->
  // "Admin". Used only when no real firstName/lastName/name is available.
  const formatNameFromEmail = (email) => {
    if (!email || typeof email !== "string") return "";
    const localPart = email.split("@")[0];
    return localPart
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  };

  const getCurrentUser = () => {
    const userObj = localStorage.getItem("user");
    if (userObj) {
      try {
        const parsedUser = JSON.parse(userObj);
        const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
        if (fullName) return fullName;
        if (parsedUser.name) return parsedUser.name;
        // ⚠️ FIX: previously `if (parsedUser.role) return parsedUser.role...`
        // here — that showed the account's ROLE (e.g. "admin" -> "Admin") as
        // if it were the logged-in person's name, so every admin-role
        // account displayed "Admin" here regardless of who was actually
        // logged in (this is why switching accounts didn't change anything
        // you saw on screen). Derive a readable name from the email instead,
        // since that's actually unique per person.
        if (parsedUser.email) return formatNameFromEmail(parsedUser.email);
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
    const storedName = localStorage.getItem("userName");
    if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
    return "User";
  };

  const getCurrentUserEmail = () => {
    const userObj = localStorage.getItem("user");
    if (userObj) {
      try {
        const parsedUser = JSON.parse(userObj);
        if (parsedUser.email) return parsedUser.email;
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
    const storedEmail = localStorage.getItem("userEmail");
    if (storedEmail && storedEmail !== "undefined" && storedEmail !== "null") return storedEmail;
    return "";
  };

  // ⚠️ FIX: "PO Generated By" / "Email Id" on the printed PO used to call
  // getCurrentUser()/getCurrentUserEmail() directly, which read the LOGGED-IN
  // BROWSER's session — i.e. whoever happens to be viewing/printing the PO
  // right now, not who actually created it. Open/print/download the same PO
  // from a different account and the document would silently relabel itself
  // with that other person's name and email. These stamps must come from the
  // saved PO record (poData[0].created_by), same source of truth as the
  // "Created By" field on the Basic tab.
  //
  // Caveat: purchase_orders only persists created_by (ideally a user id, per
  // the fix in PurchaseOrder.js's getCurrentUser()); there is no persisted
  // creator-email column, so if created_by isn't itself an email we cannot
  // reconstruct the creator's email from this record alone — that needs a
  // backend join against the users table (or a created_by_email column) to
  // be fully correct. Until then, the email line is left blank in that case
  // rather than showing the current viewer's email, which is the more
  // seriously misleading version of this bug.
  const getPoGeneratedByName = () => {
    // "PO Generated By" is the user id as stored on the record — print it
    // exactly as-is, no name formatting. (If it currently shows something
    // like "Maithili05 M" instead of a plain id, that's the created_by
    // value itself still being saved as an email/derived name rather than
    // a real user id — see the getCurrentUser() fix in PurchaseOrder.js.)
    const createdBy = poData?.[0]?.created_by;
    if (createdBy) return createdBy;
    // No saved record yet (new/unsaved PO being previewed) — fall back to
    // the current session, since in that case they genuinely are the same.
    return getCurrentUser();
  };

  const getPoGeneratedByEmail = () => {
    // Resolved server-side via a join to usererp on user_id (see
    // getPurchaseOrderById in purchaseOrder.service.js) — this is the
    // creator's actual email looked up by their user id, not a guess based
    // on what's in created_by.
    const resolvedEmail = poData?.[0]?.created_by_email;
    if (resolvedEmail) return resolvedEmail;
    const createdBy = poData?.[0]?.created_by;
    if (createdBy && createdBy.includes("@")) return createdBy;
    return getCurrentUserEmail();
  };

  useEffect(() => {
    if (poData?.length > 0) {
      loadBusinessPartner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poData]);

  const loadBusinessPartner = async () => {
    try {
      const res = await fetch(
        `${PURCHASE_API}/po/supplier-by-po/${encodeURIComponent(poId)}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`Supplier lookup failed (${res.status})`);
      const json = await res.json();
      setBpData(json?.data || {});
    } catch (err) {
      console.error(err);
    }
  };
  const thStyle = {
    ...cellStyle,
    fontWeight: "bold",
    textAlign: "center",
    fontSize: "11px",
  };



  useEffect(() => {
    loadPO();
    // Fetch the Terms & Conditions up front (not just when "View More" is
    // clicked). Print/Download never open that modal, so effectivePoSections
    // was staying empty for those flows and the terms text was silently
    // missing from the PDF unless the user happened to preview it first.
    loadPoTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Belt-and-braces for anyone printing manually (Ctrl+P) instead of via
  // the Print button's autoPrint flow above.
  useEffect(() => {
    window.addEventListener("beforeprint", revealPrintOnlyContent);
    window.addEventListener("afterprint", restorePrintOnlyContent);
    return () => {
      window.removeEventListener("beforeprint", revealPrintOnlyContent);
      window.removeEventListener("afterprint", restorePrintOnlyContent);
    };
  }, []);



  const loadVendor = async (
    vendorId
  ) => {
    try {

      const res = await fetch(
        `${PURCHASE_API}/business-partner/${vendorId}`,
        { headers: authHeaders() }
      );

      const data = await res.json();

      setVendorData(data || {});

    } catch (err) {
      console.error(err);
    }
  };

  // Builds the exact same PDF the Download button produces, from
  // whatever is currently in poWrapRef. Shared by downloadExactView() and
  // printExactView() so Print can never drift from Download again — both
  // now go through the identical server-side render.
  const buildExactViewPdfBlob = async () => {
    if (!poWrapRef.current) return null;

    // Make any relative image URLs (e.g. the logo) absolute so the
    // server-side browser can actually load them.
    const clone = poWrapRef.current.cloneNode(true);
    clone.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src");
      if (src && !/^(https?:)?\/\//i.test(src) && !src.startsWith("data:")) {
        img.setAttribute("src", new URL(src, window.location.origin).href);
      }
    });

    // Strip anything that's on-screen-only (the editable Note box, the
    // "View More" terms button) — it should never end up in the PDF.
    clone.querySelectorAll(".no-print").forEach((el) => el.remove());

    // The Terms & Conditions block below is hidden on screen (behind
    // "View More") but must always be in the actual PDF/print — un-hide
    // it in the clone the same way as pdf-timestamp, rather than relying
    // on @media print, since this HTML is rendered into a PDF server-side.
    clone.querySelectorAll(".print-only").forEach((el) => {
      el.style.display = "block";
    });

    // Stamp the exact generation time onto the PDF (this div is hidden on
    // screen; make it visible in the exported copy with a fresh timestamp
    // rather than relying on @media print, since this HTML is rendered
    // server-side into a PDF, not printed from this live page).
    clone.querySelectorAll(".pdf-timestamp").forEach((el) => {
      el.style.display = "block";
      el.textContent = `Generated on: ${new Date().toLocaleString("en-IN")}`;
    });

    const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <style>
          /* ⚠️ FIX: removed live @import of fonts.googleapis.com.
             The server-side Puppeteer render used waitUntil:
             "networkidle0", which had to wait on this outbound network
             call to fonts.googleapis.com before the page was considered
             loaded. That round-trip (from server infra, not the user's
             browser) was slow/unreliable and a likely cause of this route
             timing out — which then surfaced client-side as a false
             "CORS blocked" error once CloudFront/API Gateway killed the
             connection before Express could respond.
             Falls back to 'Courier New'/monospace below, which is
             visually near-identical for this fixed-width layout. If exact
             Courier Prime rendering is required, self-host the font file
             and reference it with a local/relative @font-face src instead
             of an external URL, so no outbound network call is needed. */
          body { margin: 0; }
          .po-wrap { font-family: 'Courier Prime', 'Courier New', monospace; }
          .po-wrap p { margin: 2px 0; }
          .po-wrap table { border-collapse: collapse; width: 100%; }
        .po-wrap tr { break-inside: avoid; page-break-inside: avoid; }
        </style>
      </head>
      <body>${clone.outerHTML}</body>
    </html>
  `;

    const poId = poData?.[0]?.purchase_order_id || "purchase-order";

    // File name: PONumber_SupplierName (illegal filename characters removed,
    // spaces turned into underscores).
    const supplierName =
      bpData?.business_partner_name || poData?.[0]?.vendor_name || "";
    const fileBase = `${poId}${supplierName ? "_" + supplierName : ""}`
      .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "")
      .replace(/\s+/g, "_");

    const res = await fetch(
      `${PURCHASE_API}/po/download-pdf-from-view`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ html, filename: fileBase, poId })
      }
    );

    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.message || "PDF generation failed");
    }

    return { blob: await res.blob(), poId, fileBase };
  };

  // Captures EXACTLY what is currently rendered on screen and asks the
  // server to turn that snapshot into a PDF — no separate template,
  // so it can never drift from the view again.
  const downloadExactView = async () => {
    if (!isPoApproved) {
      alert("This Purchase Order must be approved before it can be downloaded or printed.");
      return;
    }
    try {
      setDownloading(true);

      const result = await buildExactViewPdfBlob();
      if (!result) return;
      const { blob, fileBase } = result;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileBase}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Download error:", err);
      alert(err.message || "Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Print now goes through the exact same server-rendered PDF as Download
  // (instead of calling the browser's native window.print() on the live
  // page), so the long, multi-page Terms & Conditions section prints
  // identically to how it appears in the downloaded file — the two paths
  // used to render independently and could disagree on layout/pagination.
  const printExactView = async () => {
    if (!isPoApproved) {
      alert("This Purchase Order must be approved before it can be downloaded or printed.");
      return;
    }
    try {
      setDownloading(true);

      const result = await buildExactViewPdfBlob();
      if (!result) return;
      const { blob } = result;

      const url = window.URL.createObjectURL(blob);
      const printFrame = document.createElement("iframe");
      printFrame.style.position = "fixed";
      printFrame.style.right = "0";
      printFrame.style.bottom = "0";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "0";
      printFrame.src = url;
      document.body.appendChild(printFrame);

      printFrame.onload = () => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
        } catch (e) {
          console.error("Print frame error:", e);
          // Fallback: open the PDF in a new tab so the user can print from there.
          window.open(url, "_blank");
        }
      };

      // Clean up well after the print dialog has had time to open/close.
      setTimeout(() => {
        document.body.removeChild(printFrame);
        window.URL.revokeObjectURL(url);
      }, 60000);

    } catch (err) {
      console.error("Print error:", err);
      alert("Failed to generate PDF for printing");
    } finally {
      setDownloading(false);
    }
  };

  // (old raw-HTML iframe save removed — PO Terms & Conditions is now
  // structured JSON, edited inline in the modal below and saved via
  // savePoTermsOverride(), same pattern as QuotationView.js's terms edit.)
  //eslint-disable-next-line
  const _unused_legacySaveDocs = async () => {
    try {

      const iframe =
        document.querySelector("iframe");

      const iframeDoc =
        iframe.contentWindow.document;

      const html =
        iframeDoc.documentElement.outerHTML;

      const res = await fetch(
        `${PURCHASE_API}/po/save-po-docs`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json"
          },
          body: JSON.stringify({
            content: html
          })
        }
      );

      const data =
        await res.json();

      console.log(data);

      alert("Saved Successfully");

    } catch (err) {

      console.error(err);

      alert("Save Failed");
    }
  };



  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
        .po-wrap { font-family: 'Courier Prime', 'Courier New', monospace; }
        .po-wrap p { margin: 2px 0; }
        .po-wrap table { border-collapse: collapse; width: 100%; }
        .po-wrap tr { break-inside: avoid; page-break-inside: avoid; }
        /* Hidden on screen, shown only when printed / exported to PDF */
        .pdf-timestamp { display: none; }
        .print-only { display: none; }
        @media print {
          .no-print { display: none !important; }
          .pdf-timestamp { display: block !important; }
          .print-only { display: block !important; }
        }
      `}</style>

      <div style={{ maxWidth: "960px", margin: "0 auto 10px", textAlign: "right" }}>
        <button
          className="btn btn-dark btn-sm"
          onClick={downloadExactView}
          disabled={downloading || !isPoApproved}
          title={isPoApproved ? "" : "Available after approval"}
        >
          {downloading ? "Generating PDF..." : "Download PDF"}
        </button>
      </div>

      <div
        ref={poWrapRef}
        className="po-wrap"
        style={{
          fontSize: "13px",
          color: "#000",
          background: "#fff",
          padding: "24px 30px",
          maxWidth: "960px",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        {/* ── HEADER: no border, 3 columns ── */}
        <div style={{ display: "flex", gap: "30px", alignItems: "flex-start", marginBottom: "20px" }}>

          {/* Left: company info — flex:1 + 30px gap mirrors the Supplier / PO box row below, so the logo column lines up with the PO Details box */}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", marginBottom: "4px", fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif" }}>{unit1Name}</div>
            {unit1AddressLines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
            <br />
            {/* TEL/GST/CIN sourced from Unit 1 (Regd. Office) in
                Configuration > Location Master, same as the address above.
                Falls back to the static values only if Location Master
                hasn't been set up yet. */}
            <p>TEL NO.{unit1Location?.tel_no || "080-35742421"}</p>
            <p>GST NO. {unit1Location?.gst_no || "29AABCM5279M1ZT"}</p>
            <p>CIN NO.{unit1Location?.cin_no || "U64203KA1978PLC003385"}</p>
            {/* 🆕 IEC No. — only relevant for Import POs, sourced from the
                same Unit 1 record in Configuration > Location Master. */}
            {docCurrency && !docCurrency.isHome && unit1Location?.iec_no && (
              <p>IEC NO.{unit1Location.iec_no}</p>
            )}
          </div>

          {/* Logo — centred over the PO Details box (same column width & position) */}
          <div style={{ textAlign: "center", flex: 1 }}>
            <img
              src={sbeeLogo}
              alt="SBEE Logo"
              style={{ maxWidth: "260px", width: "100%", height: "auto", display: "inline-block" }}
            />
          </div>


        </div>

        {/* ── SUPPLIER (no border) + PURCHASE ORDER BOX ── */}
        <div
          style={{
            display: "flex",
            gap: "30px",
            alignItems: "flex-start",
            marginBottom: "16px",
          }}
        >
          {/* LEFT : SUPPLIER */}
          <div style={{ flex: 1 }}>
            <p>
              <b>SUPPLIER:</b>
            </p>

            <br />

            <p>
              {bpData?.business_partner_name || ""}
            </p>

            {/* Address uses the same font, size and colour as the supplier name above */}
            <p style={{ marginBottom: "8px" }}>
              {/* 🔧 FIX: was reading billing_* (Billing tab), but Suppliers
                  only use the single "Address" tab (address_line1..zip) —
                  see businessPartner_service.js. Billing/Shipping aren't
                  populated for Supplier-type partners. */}
              {bpData?.address_line1 || ""}
              <br />
              {bpData?.address_line2 || ""}
              <br />
              {bpData?.address_city || ""}{" "}
              {bpData?.address_state || ""}{" "}
              {bpData?.address_zip || ""}
            </p>

            <p>
              <b>GST No : </b>
              {poData?.[0]?.gstin || bpData?.gstin || ""}
            </p>

            <br />

            <p>
              <b>ATTENTION :</b>{" "}
              <span style={{ fontSize: "11px" }}>
                {bpData?.contact_person_name?.replace(/[[\]"]/g, "") || ""}
              </span>
            </p>

            <p>
              <b>DESIGNATION :</b>{" "}
              <span style={{ fontSize: "11px" }}>
                {/* Fetched from Business Partner. ⚠️ Column name assumed —
                    change here if the BP table uses a different one. */}
                {(() => {
                  // Picks whichever Business Partner field has "designation"
                  // in its name (Partner Contacts popup column).
                  const key = Object.keys(bpData || {}).find((k) =>
                    /designation/i.test(k)
                  );
                  return key && bpData[key] != null
                    ? bpData[key].toString().replace(/[[\]"]/g, "")
                    : "";
                })()}
              </span>
            </p>

            <p>
              <b>EMAIL - </b>
              {bpData?.email?.replace(/[[\]"]/g, "") || ""}
            </p>

            <p>
              <b>PHONE - </b>
              {(poData?.[0]?.phone || bpData?.phone || bpData?.contact_details || "")
                .toString()
                .replace(/[[\]"]/g, "")}
            </p>

            <br />

            <p>
              REF: Your offer / quote No.&nbsp;
              <u>
                {poData?.[0]?.quote_no || ""}
              </u>

              &nbsp;&nbsp; Date:&nbsp;

              <u>
                {poData?.[0]?.quote_date
                  ? new Date(poData[0].quote_date)
                    .toLocaleDateString("en-GB")
                  : ""}
              </u>
            </p>

            <div
              style={{
                fontSize: "10px",
                color: "#444",
                paddingLeft: "150px",
                marginTop: "2px",
              }}
            >

              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;

            </div>
          </div>

          {/* RIGHT : PURCHASE ORDER */}
          <div
            style={{
              flex: 1,
              border: "1px solid #000",
              marginTop: "-80px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                borderBottom: "1px solid #000",
                padding: "6px 8px",
                fontWeight: "bold",
                fontSize: "14px",
                letterSpacing: "3px",
              }}
            >
              PURCHASE ORDER
            </div>

            <div style={{ padding: "10px 14px", fontSize: "13px" }}>

              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>
                  PO NO
                </div>
                <div>
                  : {poData?.[0]?.purchase_order_id || ""}
                </div>
              </div>

              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>
                  PO Revision No.
                </div>
                <div>
                  : {poData?.[0]?.po_revision_no ?? "0"}
                </div>
              </div>

              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>
                  PR Number
                </div>
                <div>
                  :{" "}
                  {Array.from(
                    new Set(
                      (displayItems || [])
                        .map((item) => item.purchase_request_no)
                        .filter(Boolean)
                    )
                  ).map((prNo, i, arr) => (
                    <React.Fragment key={prNo}>
                      {prNo}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>
                  Date
                </div>
                <div>
                  : {
                    poData?.[0]?.created_at
                      ? new Date(poData[0].created_at)
                        .toLocaleDateString("en-GB")
                      : ""
                  }
                </div>
              </div>

              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>
                  Supplier No.
                </div>
                <div>
                  : {bpData?.partner_code || ""}
                </div>
              </div>

              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>
                  PO Generated By
                </div>
                <div>
                  : {getPoGeneratedByName()}
                </div>

              </div>

              <div style={{ display: "flex", marginBottom: "10px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>
                  Email Id
                </div>
                <div>
                  : {getPoGeneratedByEmail()}
                </div>
              </div>

              {/* Contact Details — from Configuration > Business Partner Responsible */}
              <div style={{ fontWeight: "bold", marginTop: "10px", marginBottom: "6px", textDecoration: "underline" }}>
                Contact Details
              </div>
              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>Name</div>
                <div>: {poData?.[0]?.contact_name || ""}</div>
              </div>
              <div style={{ display: "flex", marginBottom: "6px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>Email Id</div>
                <div>: {poData?.[0]?.contact_email || ""}</div>
              </div>
              <div style={{ display: "flex", marginBottom: "10px" }}>
                <div style={{ width: "140px", fontWeight: "bold" }}>Contact No.</div>
                <div>: {poData?.[0]?.contact_phone || ""}</div>
              </div>

              <div style={{ marginTop: "10px" }}>
                <p style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <b>Bill to :</b>
                  {/* no-print: on-screen-only edit toggle, has no meaning on a
            printed page or in the downloaded PDF */}
                  {!editingBillTo && (
                    <button
                      type="button"
                      aria-label="Edit Bill to address"
                      className="no-print"
                      style={{ padding: 0, border: "none", background: "none", cursor: "pointer", lineHeight: 0 }}
                      onClick={() => {
                        setBillToDraft(billTo || unit1AddressLines.join("\n"));
                        setEditingBillTo(true);
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                      </svg>
                    </button>
                  )}
                </p>

                {editingBillTo ? (
                  <div className="no-print" style={{ marginBottom: "8px" }}>
                    <textarea
                      value={billToDraft}
                      onChange={(e) => setBillToDraft(e.target.value)}
                      placeholder="One address line per row…"
                      rows={4}
                      style={{
                        width: "100%",
                        fontSize: "11px",
                        fontFamily: "'Courier Prime', 'Courier New', monospace",
                        padding: "6px",
                        boxSizing: "border-box",
                        resize: "vertical",
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "4px" }}>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        disabled={savingBillTo}
                        onClick={() => setEditingBillTo(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-dark btn-sm"
                        disabled={savingBillTo}
                        onClick={saveBillTo}
                      >
                        {savingBillTo ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p
                    style={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      marginBottom: "8px",
                      lineHeight: "1.6",
                    }}
                  >
                    {billToAddressLines.map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < billToAddressLines.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                )}

                <p style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <b>Ship to :</b>
                  {!editingShipTo && (
                    <button
                      type="button"
                      aria-label="Edit Ship to address"
                      className="no-print"
                      style={{ padding: 0, border: "none", background: "none", cursor: "pointer", lineHeight: 0 }}
                      onClick={() => {
                        setShipToDraft(shipTo || unit1AddressLines.join("\n"));
                        setEditingShipTo(true);
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                      </svg>
                    </button>
                  )}
                </p>

                {editingShipTo ? (
                  <div className="no-print" style={{ marginBottom: "8px" }}>
                    <textarea
                      value={shipToDraft}
                      onChange={(e) => setShipToDraft(e.target.value)}
                      placeholder="One address line per row…"
                      rows={4}
                      style={{
                        width: "100%",
                        fontSize: "11px",
                        fontFamily: "'Courier Prime', 'Courier New', monospace",
                        padding: "6px",
                        boxSizing: "border-box",
                        resize: "vertical",
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "4px" }}>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        disabled={savingShipTo}
                        onClick={() => setEditingShipTo(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-dark btn-sm"
                        disabled={savingShipTo}
                        onClick={saveShipTo}
                      >
                        {savingShipTo ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p
                    style={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      lineHeight: "1.6",
                    }}
                  >
                    {shipToAddressLines.map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < shipToAddressLines.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* ── PO NOTE (free text, saved once per PO) ── */}
        {/* no-print: the editable textarea + Save button are for
            on-screen editing only — has no meaning on a printed page or
            in the downloaded PDF. The saved value itself, however,
            SHOULD appear in the PDF, so it's rendered separately below
            as a print-only, read-only block (previously this whole box
            was no-print, so the note was silently omitted from every
            download regardless of whether one had been saved). */}
        <div
          className="no-print"
          style={{
            border: "1px solid #000",
            padding: "8px 10px",
            marginBottom: "10px",
          }}
        >
          <div style={{ fontWeight: "bold", fontSize: "12px", marginBottom: "6px" }}>
            Note
          </div>
          <textarea
            value={poNoteDraft}
            onChange={(e) => setPoNoteDraft(e.target.value)}
            placeholder="Write any note for this PO…"
            rows={3}
            style={{
              width: "100%",
              fontSize: "12px",
              fontFamily: "'Courier Prime', 'Courier New', monospace",
              padding: "6px",
              boxSizing: "border-box",
              resize: "vertical",
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
            <button
              className="btn btn-dark btn-sm"
              disabled={savingNote}
              onClick={savePoNote}
            >
              {savingNote ? "Saving..." : "Save Note"}
            </button>
          </div>
        </div>

        {/* print-only: read-only rendering of the saved note, shown only
            in Print/the downloaded PDF. Only rendered when a note has
            actually been saved, so blank PDFs don't get an empty box. */}
        {poNote && (
          <div
            className="print-only"
            style={{
              border: "1px solid #000",
              padding: "8px 10px",
              marginBottom: "10px",
              fontSize: "12px",
              fontFamily: "'Courier Prime', 'Courier New', monospace",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "6px" }}>Note</div>
            {poNote}
          </div>
        )}

        {/* ── Ordering statement ── */}
        <div style={{ fontSize: "12px", lineHeight: "1.5", marginBottom: "6px" }}>
          We hereby order and agree to purchase from you subject to the terms and conditions indicated on the front and as per enclosure, hereof the materials/services listed hereon at the price(s) shown.
        </div>

        {/* ── MATERIAL TABLE ── */}
        <table style={{ tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: "4%" }}>SL<br />NO</th>
              <th style={{ ...thStyle, width: "9%" }}>MATERIAL<br />CODE</th>
              <th style={{ ...thStyle, width: "13%" }}>Items</th>
              <th style={{ ...thStyle, width: "19%" }}>Description</th>
              <th style={{ ...thStyle, width: "9%" }}>HSN</th>
              <th style={{ ...thStyle, width: "8%" }}>QTY</th>
              <th style={{ ...thStyle, width: "5%" }}>UOM</th>
              <th style={{ ...thStyle, width: "8%" }}>RATE</th>
              <th style={{ ...thStyle, width: "6%" }}>Dis(%)</th>
              <th style={{ ...thStyle, width: "10%" }}>Total</th>
              <th style={{ ...thStyle, width: "9%" }}>Delivery<br />Date</th>
            </tr>
          </thead>
          <tbody>
            {displayItems && displayItems.length > 0 ? (
              displayItems.map((item, index) => (
                <tr key={index}>
                  <td style={cellStyle}>
                    {index + 1}
                  </td>

                  <td style={cellStyle}>
                    {item.product_code || ""}
                  </td>

                  <td style={cellStyle}>
                    {item.description || ""}
                  </td>

                  <td style={cellStyle}>
                    {getSupplierDescription(item)}
                  </td>

                  <td style={cellStyle}>
                    {item.hsn_code || ""}
                  </td>

                  <td style={cellStyle}>
                    {item.requirement_qty || ""}
                  </td>

                  <td style={cellStyle}>
                    {item.uom || ""}
                  </td>

                  <td style={cellStyle}>
                    {item.product_cost ? formatRate(item.product_cost) : ""}
                  </td>

                  <td style={cellStyle}>
                    {item.discount || ""}
                  </td>

                  <td style={cellStyle}>
                    {item.net_price_after_discount ? formatINR(item.net_price_after_discount) : ""}
                  </td>

                  <td style={cellStyle}>
                    {formatDDMMYYYY(item.delivery_date)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="11"
                  style={{
                    ...cellStyle,
                    textAlign: "center",
                    height: "50px"
                  }}
                >
                  No Materials Found
                </td>
              </tr>
            )}

            {/* TOTAL ROW */}
            <tr>
              <td style={cellStyle}></td>{/* SL NO */}

              <td
                style={{
                  ...cellStyle,
                  fontWeight: "bold"
                }}
              >
                {/* Show the symbol only when it differs from the code, so a
                    currency with no symbol configured (e.g. EURO) isn't
                    printed twice as "EURO EURO". */}
                {String(docCurrency.symbol).trim().toLowerCase() ===
                String(docCurrency.code).trim().toLowerCase()
                  ? docCurrency.code
                  : `${docCurrency.code} ${docCurrency.symbol}`}
              </td>{/* MATERIAL CODE */}

              <td style={cellStyle}></td>{/* Items */}
              <td style={cellStyle}></td>{/* Description */}
              <td style={cellStyle}></td>{/* HSN */}

              <td
                style={{
                  ...cellStyle,
                  fontWeight: "bold"
                }}
              >
                TOTAL
              </td>{/* QTY */}

              <td style={cellStyle}></td>{/* UOM */}
              <td style={cellStyle}></td>{/* RATE */}
              <td style={cellStyle}></td>{/* Dis(%) */}

              <td
                style={{
                  ...cellStyle,
                  fontWeight: "bold"
                }}
              >
                {formatINR(
                  displayItems?.reduce(
                    (sum, item) =>
                      sum +
                      Number(
                        item.net_price_after_discount || 0
                      ),
                    0
                  )
                )}
              </td>{/* Total */}

              <td style={cellStyle}></td>{/* Delivery Date */}
            </tr>
          </tbody>
        </table>

        {/* ── TOTAL IN WORD + TAXABLE AMOUNT SUMMARY ── */}
        <div style={{ display: "flex", border: "1px solid #000", borderTop: "none", breakInside: "avoid", pageBreakInside: "avoid" }}>
          {/* LEFT: Total in Word + Terms */}
          <div style={{ flex: 1, borderRight: "1px solid #000" }}>
            <div
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid #000",
                fontSize: "12px",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                columnGap: "4px",
                rowGap: "2px",
              }}
            >
              <b style={{ whiteSpace: "nowrap" }}>Total In Word :</b>
              <span>{docCurrency.isHome ? numberToIndianWords(grandTotalAfterTax) : numberToForeignWords(grandTotalAfterTax, docCurrency.code)}</span>
            </div>
            <div style={{ padding: "10px 14px" }}>
              <p style={{ margin: "0 0 6px 0" }}>
                <b>Payment Terms :</b> {poData?.[0]?.payment_terms || ""}
              </p>
              <p style={{ margin: "0 0 6px 0" }}>
                <b>Mode of Transport :</b>{" "}
                {poData?.[0]?.mode_of_transport || ""}
              </p>
              <p style={{ margin: "0 0 6px 0" }}>
                <b>Type :</b> {poData?.[0]?.delivery_type || ""}
              </p>
              <p style={{ margin: "0 0 6px 0" }}>
                <b>Freight Terms :</b>{" "}
                {poData?.[0]?.freight_terms || ""}
              </p>
              <p style={{ margin: "0" }}>
                <b>Insurance Terms :</b>{" "}
                {poData?.[0]?.insurance_terms || ""}
              </p>
            </div>
          </div>

          {/* RIGHT: Taxable Amount / GST / Total summary */}
          <div style={{ width: "300px" }}>
            {(() => {
              const taxableAmount = displayItems?.reduce(
                (sum, item) => sum + Number(item.net_price_after_discount || 0),
                0
              ) || 0;

              // 🆕 Dynamic GST breakup — sum each PO's actual tax types
              // (from purchase_order_tax, returned as item.taxes on every
              // line by GET /po/:poId) across all approved items, instead
              // of always assuming CGST + SGST only. An Import PO with
              // Basic Customs Duty / Social Welfare Surcharge / Other
              // Customs Charges now shows those 3 lines here by name,
              // and a Domestic PO still shows CGST/SGST (or IGST) — same
              // rule the Product tab's own grid already follows.
              const taxTotalsByType = {};
              const taxOrder = [];
              displayItems?.forEach((item) => {
                (item.taxes || []).forEach((t) => {
                  const type = (t.type || "Tax").trim();
                  if (!(type in taxTotalsByType)) {
                    taxTotalsByType[type] = 0;
                    taxOrder.push(type);
                  }
                  taxTotalsByType[type] += Number(t.value || 0);
                });
              });

              const formatPct = (n) => {
                const val = Math.round(n * 100) / 100;
                return val % 1 === 0 ? val.toFixed(0) : val.toFixed(2);
              };

              let taxLines = taxOrder.map((type) => ({
                label: type,
                amount: taxTotalsByType[type],
                percent: taxableAmount > 0 ? formatPct((taxTotalsByType[type] / taxableAmount) * 100) : "0",
              }));

              // Fallback for older saved POs with no purchase_order_tax
              // rows yet (pre-dynamic-tax-table) — same fixed CGST/SGST/
              // IGST columns this used to always show.
              if (taxLines.length === 0) {
                const cgstTotal = displayItems?.reduce((s, i) => s + Number(i.cgst_amount || 0), 0) || 0;
                const sgstTotal = displayItems?.reduce((s, i) => s + Number(i.sgst_amount || 0), 0) || 0;
                const igstTotal = displayItems?.reduce((s, i) => s + Number(i.igst_amount || 0), 0) || 0;
                taxLines = [
                  { label: "CGST", amount: cgstTotal, percent: taxableAmount > 0 ? formatPct((cgstTotal / taxableAmount) * 100) : "0" },
                  { label: "SGST", amount: sgstTotal, percent: taxableAmount > 0 ? formatPct((sgstTotal / taxableAmount) * 100) : "0" },
                  ...(igstTotal > 0 ? [{ label: "IGST", amount: igstTotal, percent: taxableAmount > 0 ? formatPct((igstTotal / taxableAmount) * 100) : "0" }] : []),
                ];
              }

              const totalTax = taxLines.reduce((sum, t) => sum + t.amount, 0);
              const totalAfterTax = grandTotalAfterTax;
              const roundOff = totalAfterTax - (taxableAmount + totalTax);

              const rowStyle = {
                display: "flex",
                justifyContent: "space-between",
                padding: "5px 10px",
                borderBottom: "1px solid #000",
                whiteSpace: "nowrap",
                gap: "10px",
              };

              return (
                <>
                  <div style={rowStyle}>
                    <span><b>Taxable Amount</b></span>
                    <span>{formatINR(taxableAmount)}</span>
                  </div>
                  {taxLines.map((t) => (
                    <div style={rowStyle} key={t.label}>
                      <span>{t.label} ({t.percent}%)</span>
                      <span>{formatINR(t.amount)}</span>
                    </div>
                  ))}
                  <div style={rowStyle}>
                    <span><b>Total Tax</b></span>
                    <span>{formatINR(totalTax)}</span>
                  </div>
                  <div style={rowStyle}>
                    <span>Round off Amount</span>
                    <span>{formatINR(roundOff)}</span>
                  </div>
                  <div style={{ ...rowStyle, borderBottom: "none", fontWeight: "bold" }}>
                    <span>Total Amount After Tax</span>
                    <span>{formatINR(totalAfterTax)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* ── TERMS AND CONDITIONS (just above jurisdiction) ── */}
        <div
          style={{
            border: "1px solid #000",
            borderTop: "none",
          }}
        >
          {/* Terms and Conditions */}
          <div
            style={{
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              Terms and Conditions
            </div>

            {/* The PO's own short Terms & Conditions text (Basic tab,
                terms_and_conditions column) — always shown here, on
                screen and in print/PDF, same as Payment Terms etc.
                further up. The "View More" button below opens the
                separate, longer legal-clauses template instead. */}
            {editingTermsText ? (
              <div className="no-print" style={{ width: "100%", marginBottom: "8px" }}>
                <textarea
                  className="form-control"
                  rows={5}
                  value={termsTextDraft}
                  onChange={(e) => setTermsTextDraft(e.target.value)}
                  disabled={savingTermsText}
                  autoFocus
                  style={{ width: "100%", fontSize: "inherit", fontFamily: "inherit" }}
                />
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleCancelTermsText}
                    disabled={savingTermsText}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-dark btn-sm"
                    onClick={saveTermsText}
                    disabled={savingTermsText}
                  >
                    {savingTermsText ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: "8px", whiteSpace: "pre-line" }}>
                {poData?.[0]?.terms_and_conditions || ""}
              </div>
            )}

            {/* On-screen only — never printed / exported to PDF */}
            {!editingTermsText && (
              <button
                className="btn btn-outline-dark btn-sm no-print"
                style={{ marginBottom: "8px" }}
                onClick={handleEditTermsText}
              >
                Edit
              </button>
            )}

            {/* Stamped only onto the printed page / downloaded PDF — hidden
                on screen (see .pdf-timestamp in the <style> block above).
                downloadExactView() fills in the live time for the PDF export
                path, since it clones the DOM rather than printing it. Sits
                directly below the Terms and Conditions text. */}
            <div
              className="pdf-timestamp"
              style={{ fontSize: "10px", marginBottom: "8px", color: "#000" }}
            >
              Generated on: {new Date().toLocaleString("en-IN")}
            </div>

            {/* no-print: this is an on-screen-only control that opens a
                modal — it has no purpose (and no working click handler)
                on a printed page or in the downloaded PDF */}
            <button
              className="btn btn-dark btn-sm no-print"
              onClick={() => {
                setShowDocs(true);
                loadPoTerms();
              }}
            >
              View More
            </button>
          </div>

          {/* print-only: the full Terms & Conditions text, hidden on
              screen (the "View More" popup is used for browsing/editing
              there instead) but always included in Print and the
              downloaded PDF — previously this content only existed
              inside the popup, so it was missing unless the popup had
              already been opened at least once in the session.
              Always rendered (not gated on data being ready) so there's
              always a real element for revealPrintOnlyContent() to find
              and show — a conditionally-mounted div can't be forced
              visible if it was never in the DOM in the first place. */}
          <div className="print-only" style={{ padding: "0 10px 10px", fontSize: "11px" }}>
            {templateLoading ? (
              <p style={{ margin: 0 }}>Loading Terms &amp; Conditions…</p>
            ) : effectivePoSections.length === 0 ? (
              <p style={{ margin: 0 }}>No Terms &amp; Conditions template has been configured yet.</p>
            ) : (
              effectivePoSections.map((section, sIdx) => (
                <div
                  key={section.id || sIdx}
                  style={{
                    marginBottom: "14px",
                    // "General Terms & Conditions of Purchase Order for Supply
                    // of Material / Equipment" always starts on a fresh sheet.
                    ...(/general\s+terms/i.test(section.heading || "")
                      ? { breakBefore: "page", pageBreakBefore: "always" }
                      : {}),
                  }}
                >
                  <h4 style={{ textAlign: "center", fontSize: "12.5px", margin: "0 0 6px" }}>
                    {section.heading}
                  </h4>
                  {section.type === "list" ? (
                    <ol style={{ paddingLeft: "18px", margin: 0 }}>
                      {(section.items || []).map((item, i) => (
                        <li key={i} style={{ marginBottom: "6px", textAlign: "justify" }}>
                          {item}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <>
                      {section.intro && <p style={{ textAlign: "justify" }}>{section.intro}</p>}
                      {(section.clauses || []).map((clause, cIdx) => (
                        <div key={cIdx} style={{ marginBottom: "6px" }}>
                          <h5 style={{ fontSize: "11.5px", margin: "8px 0 4px" }}>
                            {clause.number}. {clause.heading}
                          </h5>
                          {(clause.paragraphs || []).map((p, pIdx) => (
                            <p key={pIdx} style={{ textAlign: "justify", margin: "2px 0" }}>
                              {p}
                            </p>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── JURISDICTION LINE (always last) ── */}
        <div
          style={{
            border: "1px solid #000",
            borderTop: "none",
            padding: "8px 14px",
            fontSize: "12px",
            fontStyle: "italic",
            fontWeight: "bold",
          }}
        >
          <hr style={{ margin: "0 0 8px 0", border: "none", borderTop: "1px solid #000" }} />
          ALL DISPUTES RELATING TO THIS PURCHASE ORDER IS SUBJECT TO BANGALORE JURISDICTION
        </div>


        {/* no-print: this is the on-screen "Terms and Conditions" popup, which
    renders the same effectivePoSections data (including the "NOTE"
    list) already shown in the .print-only block below. Without this
    class, buildExactViewPdfBlob() only strips .no-print elements from
    poWrapRef before capture, so if this modal happened to be open
    when Download/Print was triggered, its content got captured
    alongside the .print-only block, duplicating the NOTE section and
    General T&Cs in the PDF. */}
        {showDocs && (
          <div
            className="no-print"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.5)",
              zIndex: 99999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "90%",
                height: "90%",
                background: "#fff",
                padding: "10px",
              }}
            >

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "10px"
                }}
              >
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {savedPoSections
                    ? "Showing this PO's own edited Terms & Conditions."
                    : "Showing the shared default template (Configuration > Terms & Conditions)."}
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  {!editingPoTerms ? (
                    <button className="btn btn-dark btn-sm" onClick={handleEditPoTerms} disabled={templateLoading}>
                      ✎ Edit for this PO
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleCancelPoTerms}
                        disabled={savingPoTerms}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={savePoTermsOverride}
                        disabled={savingPoTerms}
                      >
                        {savingPoTerms ? "Saving…" : "Save for this PO"}
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => { setShowDocs(false); setEditingPoTerms(false); }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* ── PO Terms & Conditions — shows the shared Config template by
             default, or this PO's own saved override if one exists.
             Editing + saving here affects ONLY this PO. ── */}
              <div style={{ width: "100%", height: "88%", overflowY: "auto", padding: "10px 20px" }}>
                {templateLoading ? (
                  <p>Loading…</p>
                ) : effectivePoSections.length === 0 ? (
                  <p>No Terms & Conditions template has been configured yet.</p>
                ) : (
                  (editingPoTerms ? poTermsDraft : effectivePoSections).map((section, sIdx) => (
                    <div key={section.id || sIdx} style={{ marginBottom: "24px" }}>
                      <h2 style={{ textAlign: "center", fontSize: "16px" }}>{section.heading}</h2>

                      {section.type === "list" ? (
                        <ol style={{ paddingLeft: "22px" }}>
                          {(section.items || []).map((item, i) =>
                            editingPoTerms ? (
                              <li key={i} style={{ marginBottom: "10px" }}>
                                <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                                  <textarea
                                    className="form-control"
                                    value={item}
                                    onChange={(e) => updateDraftListItem(sIdx, i, e.target.value)}
                                    rows={Math.max(1, Math.ceil((item || "").length / 100))}
                                    style={{ fontSize: "13px", flex: 1 }}
                                  />
                                  <RowActions
                                    onAdd={() => addDraftListItem(sIdx, i)}
                                    onRemove={() => removeDraftListItem(sIdx, i)}
                                  />
                                </div>
                              </li>
                            ) : (
                              <li key={i} style={{ marginBottom: "10px", textAlign: "justify" }}>
                                {item}
                              </li>
                            )
                          )}
                          {editingPoTerms && (section.items || []).length === 0 && (
                            <div style={{ listStyle: "none" }}>
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                onClick={() => addDraftListItem(sIdx, -1)}
                              >
                                + Add row
                              </button>
                            </div>
                          )}
                        </ol>
                      ) : (
                        <>
                          {section.intro && <p style={{ textAlign: "justify" }}>{section.intro}</p>}
                          {(section.clauses || []).map((clause, cIdx) => (
                            <div key={cIdx} style={{ marginBottom: "10px" }}>
                              <h3 style={{ fontSize: "13.5px", margin: "14px 0 6px 0" }}>
                                {clause.number}. {clause.heading}
                              </h3>
                              {(clause.paragraphs || []).map((p, pIdx) =>
                                editingPoTerms ? (
                                  <div key={pIdx} style={{ display: "flex", gap: "6px", alignItems: "flex-start", marginBottom: "8px" }}>
                                    <textarea
                                      className="form-control"
                                      value={p}
                                      onChange={(e) => updateDraftParagraph(sIdx, cIdx, pIdx, e.target.value)}
                                      rows={Math.max(1, Math.ceil((p || "").length / 100))}
                                      style={{ fontSize: "13px", flex: 1 }}
                                    />
                                    <RowActions
                                      onAdd={() => addDraftParagraph(sIdx, cIdx, pIdx)}
                                      onRemove={() => removeDraftParagraph(sIdx, cIdx, pIdx)}
                                    />
                                  </div>
                                ) : (
                                  <p key={pIdx} style={{ textAlign: "justify" }}>{p}</p>
                                )
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default PODocsView;